import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format, addMonths, subMonths, addDays, subDays, startOfMonth, endOfMonth, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfYear, endOfYear, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';
import { PeriodType } from '@/hooks/useTransactionFilters';
import { cn } from '@/lib/utils';
import { getDisplayedText } from '@/lib/utils/dateUtils';
import { handlePeriodSelect as handlePeriodSelectUtil, handleNavigateMonth as handleNavigateMonthUtil, PERIOD_OPTIONS } from '@/lib/utils/dateFilterUtils';

interface VencimentoDateFilterProps {
  periodType: PeriodType;
  setPeriodType: (type: PeriodType) => void;
  dateRange: DateRange | { from?: Date; to?: Date } | undefined;
  setDateRange: (range: DateRange | { from?: Date; to?: Date } | undefined) => void;
  appliedDateRange: DateRange | { from?: Date; to?: Date } | undefined;
  setAppliedDateRange: (range: DateRange | { from?: Date; to?: Date } | undefined) => void;
}

export function VencimentoDateFilter({
  periodType,
  setPeriodType,
  dateRange,
  setDateRange,
  appliedDateRange,
  setAppliedDateRange,
}: VencimentoDateFilterProps) {
  const now = new Date();
  const [isCustomPopoverOpen, setIsCustomPopoverOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);

  // Determinar o mês/ano atual baseado no filtro aplicado
  const currentDisplayDate = useMemo(() => {
    if (appliedDateRange?.from) {
      const fromDate = appliedDateRange.from instanceof Date
        ? appliedDateRange.from
        : new Date(appliedDateRange.from);
      return fromDate;
    }
    return now;
  }, [appliedDateRange, now]);

  // Determinar o texto a ser exibido baseado no período selecionado
  const displayedText = useMemo(() => {
    return getDisplayedText(periodType, appliedDateRange, currentDisplayDate);
  }, [periodType, appliedDateRange, currentDisplayDate]);

  // Verificar se há filtro ativo
  const hasActiveFilter = periodType !== 'all' || (appliedDateRange?.from && appliedDateRange?.to);

  const onPeriodSelect = (period: PeriodType) => {
    const newDateRange = handlePeriodSelectUtil(period, now);
    setPeriodType(period);
    setAppliedDateRange(newDateRange);
    setDateRange(newDateRange);
  };

  const onNavigateMonth = (direction: 'prev' | 'next') => {
    if (periodType === 'all') {
      return; // Não navegar quando estiver em "Todo o período"
    }

    const { newDateRange, newPeriodType } = handleNavigateMonthUtil(direction, periodType, appliedDateRange, currentDisplayDate);
    setPeriodType(newPeriodType);
    setAppliedDateRange(newDateRange);
    setDateRange(newDateRange);
  };

  return (
    <div className={cn(
      "flex items-center gap-1 border border-input bg-background rounded-md px-2 py-[7px] text-sm input-focus-internal",
      hasActiveFilter && "border-primary"
    )}>
        {/* Botão de navegação anterior */}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 hover:bg-accent disabled:opacity-50 p-0"
          onClick={() => onNavigateMonth('prev')}
          disabled={periodType === 'all'}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>

        {/* Dropdown com mês/ano e períodos */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "flex-1 justify-between h-auto py-0 px-2 font-medium hover:bg-accent rounded-sm transition-colors",
                hasActiveFilter && "font-semibold text-blue-800"
              )}
            >
              <span className="text-sm">{displayedText}</span>
              <ChevronDown className="h-3.5 w-3.5 ml-1 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {PERIOD_OPTIONS.map((option) => {
              if (option.value === 'custom') {
                // Render Popover para opção personalizada
                return (
                  <Popover key={option.value} open={isCustomPopoverOpen} onOpenChange={setIsCustomPopoverOpen}>
                    <PopoverTrigger asChild>
                      <div
                        className={cn(
                          "px-2 py-1.5 text-sm cursor-pointer hover:bg-accent",
                          periodType === 'custom' && "bg-accent font-medium"
                        )}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span>{option.label}</span>
                          <Calendar className="h-3.5 w-3.5 ml-2 opacity-70" />
                        </div>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="p-3">
                        <p className="text-sm font-medium mb-2">Selecione o período</p>
                        <CalendarComponent
                          mode="range"
                          selected={customDateRange || dateRange}
                          onSelect={(range) => {
                            setCustomDateRange(range);
                            if (range?.from && range?.to) {
                              const newRange: DateRange = {
                                from: startOfDay(range.from),
                                to: endOfDay(range.to),
                              };
                              setPeriodType('custom');
                              setAppliedDateRange(newRange);
                              setDateRange(newRange);
                              setIsCustomPopoverOpen(false);
                            }
                          }}
                          numberOfMonths={2}
                          locale={ptBR}
                          className="rounded-md border"
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              }

              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onPeriodSelect(option.value)}
                  className={cn(
                    "cursor-pointer",
                    periodType === option.value && "bg-accent font-medium"
                  )}
                >
                  {option.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Botão de navegação próximo */}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 hover:bg-accent disabled:opacity-50 p-0"
          onClick={() => onNavigateMonth('next')}
          disabled={periodType === 'all'}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
    </div>
  );
}

